import { calendarBookingActionTarget } from './calendar-bookings';
import { CALENDAR_ITEM_KINDS, type CalendarItem, type CalendarItemKind } from './calendar-item';

/**
 * The calendar item a chat message was sent FROM, as the Calendar drew it when the owner tapped it. It rides the
 * send as `references.calendarItem`, which the harness keeps on the row's metadata (`metadata.references`), so the
 * owner's bubble is drawn as this card live and after a reload alike. A build that does not know the key shows the
 * message's plain seed text instead.
 *
 * It is a snapshot, not a live read: the card shows what the owner tapped, and the reply under it is where the
 * item's current state is told. The words are already in the reader's language; times are instants drawn in the
 * reader's zone.
 */
export interface CalendarItemCard {
  kind: CalendarItemKind;
  title: string;
  start: number;
  end: number;
  allDay: boolean;
  /** The line under the title (who, where, which site), as the Calendar's own rows show it. */
  meta?: string;
  /** The zone the Calendar drew the times in, so the card shows the same clock the owner tapped. */
  timeZone?: string;
  /** The item's status badges as the Calendar's rows show them (needs approval, not paid, tentative, done…). */
  tags?: CalendarItemCardTag[];
  /** A meeting's video link, so the card can join the call in one tap rather than through a Maestro turn. */
  joinUrl?: string;
  /** A meeting's calendar event id, which finds it in the Meetings channel's schedule for the card's Maestro join. */
  meetingEventId?: string;
  /** A booking's own lines, drawn as the "Appointment detail" design draws them. */
  booking?: CalendarItemCardBooking;
  /**
   * Which booking this is, kept only when the Calendar's details card offered it buttons. The chat card reads the
   * booking live by these ids and decides its buttons from what it reads, never from the card's own snapshot.
   */
  bookingRef?: CalendarItemCardBookingRef;
}

export interface CalendarItemCardBookingRef {
  siteId: string;
  bookingId: string;
}

const ID_MAX = 200;

export const CARD_PAYMENTS = ['paid', 'not_paid', 'partially_paid', 'refunded'] as const;

export interface CalendarItemCardBooking {
  staff?: string[];
  /** A class's places instead of its clients, which can run to dozens. */
  places?: { booked: number; total: number };
  clients?: string[];
  price?: { amount: string; currency: string };
  /** The one client's payment; a class's is the owing count among its badges. */
  payment?: (typeof CARD_PAYMENTS)[number];
  /** The Wix site the booking is on. */
  site?: string;
}

/** The badge styles a calendar row's status uses — the only ones a card can carry. */
export const CARD_TAG_VARIANTS = ['waiting-on-a-reply', 'waiting-on-you', 'in-progress', 'done', 'paused', 'failed'] as const;

export interface CalendarItemCardTag {
  label: string;
  variant: (typeof CARD_TAG_VARIANTS)[number];
}

const TAGS_MAX = 6;
const TAG_TEXT_MAX = 60;

const TEXT_MAX = 300;

const NAMES_MAX = 6;
const CLIENTS_MAX = 3;
const NAME_MAX = 60;

export function readCalendarItemCard(value: unknown): CalendarItemCard | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const card = value as Record<string, unknown>;
  const { kind, title, start, end, allDay, meta, timeZone, tags, joinUrl, meetingEventId, booking, bookingRef } = card;
  if (typeof kind !== 'string' || !(CALENDAR_ITEM_KINDS as readonly string[]).includes(kind)) return undefined;
  if (typeof title !== 'string' || !title || typeof start !== 'number' || typeof end !== 'number' || !Number.isFinite(start) || !Number.isFinite(end)) {
    return undefined;
  }
  return {
    kind: kind as CalendarItemKind,
    title: title.slice(0, TEXT_MAX),
    start,
    end,
    allDay: allDay === true,
    ...(typeof meta === 'string' && meta ? { meta: meta.slice(0, TEXT_MAX) } : {}),
    ...(typeof timeZone === 'string' && isZone(timeZone) ? { timeZone } : {}),
    ...(readTags(tags).length > 0 ? { tags: readTags(tags) } : {}),
    ...(kind === 'meeting' && isJoinUrl(joinUrl) ? { joinUrl } : {}),
    ...(kind === 'meeting' && typeof meetingEventId === 'string' && meetingEventId && meetingEventId.length <= EVENT_ID_MAX ? { meetingEventId } : {}),
    ...(kind === 'booking' && readBooking(booking) ? { booking: readBooking(booking) } : {}),
    ...(kind === 'booking' && readBookingRef(bookingRef) ? { bookingRef: readBookingRef(bookingRef) } : {}),
  };
}

const isId = (value: unknown): value is string => typeof value === 'string' && !!value && value.length <= ID_MAX;

function readBookingRef(value: unknown): CalendarItemCardBookingRef | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const { siteId, bookingId } = value as Record<string, unknown>;
  return isId(siteId) && isId(bookingId) ? { siteId, bookingId } : undefined;
}

/** The booking read for a card, when it is the one the card names: a booking on the same site with the same id. */
export function liveCardBooking(card: Pick<CalendarItemCard, 'kind' | 'bookingRef'>, booking: CalendarItem | undefined): CalendarItem | undefined {
  const ref = card.kind === 'booking' ? card.bookingRef : undefined;
  return ref && booking?.kind === 'booking' && booking.ref.siteId === ref.siteId && booking.ref.bookingId === ref.bookingId ? booking : undefined;
}

function readBooking(value: unknown): CalendarItemCardBooking | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const { staff, places, clients, price, payment, site } = value as Record<string, unknown>;
  const booking: CalendarItemCardBooking = {
    ...(readNames(staff, NAMES_MAX).length > 0 ? { staff: readNames(staff, NAMES_MAX) } : {}),
    ...(isPlaces(places) ? { places: { booked: places.booked, total: places.total } } : {}),
    ...(readNames(clients, CLIENTS_MAX).length > 0 ? { clients: readNames(clients, CLIENTS_MAX) } : {}),
    ...(isPrice(price) ? { price: { amount: price.amount, currency: price.currency } } : {}),
    ...(typeof payment === 'string' && (CARD_PAYMENTS as readonly string[]).includes(payment) ? { payment: payment as CalendarItemCardBooking['payment'] } : {}),
    ...(typeof site === 'string' && site ? { site: site.slice(0, TEXT_MAX) } : {}),
  };
  return Object.keys(booking).length > 0 ? booking : undefined;
}

function readNames(value: unknown, max: number): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((name): name is string => typeof name === 'string' && !!name).slice(0, max).map((name) => name.slice(0, NAME_MAX));
}

function isPlaces(value: unknown): value is { booked: number; total: number } {
  if (!value || typeof value !== 'object') return false;
  const { booked, total } = value as Record<string, unknown>;
  return Number.isInteger(booked) && Number.isInteger(total) && (booked as number) >= 0 && (total as number) > 0;
}

/** The same shape the Calendar keeps: a plain decimal amount and an ISO 4217 code, since it is formatted as money. */
function isPrice(value: unknown): value is { amount: string; currency: string } {
  if (!value || typeof value !== 'object') return false;
  const { amount, currency } = value as Record<string, unknown>;
  return typeof amount === 'string' && /^\d{1,9}(\.\d{1,4})?$/.test(amount) && typeof currency === 'string' && /^[A-Z]{3}$/.test(currency);
}

const EVENT_ID_MAX = 1024;

const JOIN_URL_MAX = 2000;

/** Only an https link opens from a card: the card is written by the sender's client, so it is input like any other. */
function isJoinUrl(value: unknown): value is string {
  if (typeof value !== 'string' || value.length > JOIN_URL_MAX) return false;
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
}

function readTags(value: unknown): CalendarItemCardTag[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter(
      (tag): tag is CalendarItemCardTag =>
        !!tag &&
        typeof tag === 'object' &&
        typeof tag.label === 'string' &&
        !!tag.label &&
        (CARD_TAG_VARIANTS as readonly string[]).includes(tag.variant),
    )
    .slice(0, TAGS_MAX)
    .map((tag) => ({ label: tag.label.slice(0, TAG_TEXT_MAX), variant: tag.variant }));
}

/** The card a persisted or pushed USER row carries, from the harness's `metadata.references`. */
export function calendarItemCardOf(metadata: unknown): CalendarItemCard | undefined {
  if (!metadata || typeof metadata !== 'object') return undefined;
  const references = (metadata as { references?: unknown }).references;
  if (!references || typeof references !== 'object') return undefined;
  return readCalendarItemCard((references as { calendarItem?: unknown }).calendarItem);
}

function isZone(timeZone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
    return true;
  } catch {
    return false;
  }
}

/** The card for an item as the Calendar draws it: its title and meta line in the reader's words, its times in `timeZone`. */
export function calendarItemCardFor(
  item: CalendarItem,
  text: { title: string; meta: string; tags: { label: string; variant: string }[] },
  timeZone: string,
): CalendarItemCard {
  const tags = readTags(text.tags);
  const bookingRef = bookingRefOf(item);
  return {
    kind: item.kind,
    title: text.title,
    start: item.start,
    end: item.end,
    allDay: item.allDay,
    ...(text.meta ? { meta: text.meta } : {}),
    timeZone,
    ...(tags.length > 0 ? { tags } : {}),
    ...(item.kind === 'meeting' && isJoinUrl(item.videoLink) ? { joinUrl: item.videoLink } : {}),
    ...(item.kind === 'meeting' && item.ref.eventId ? { meetingEventId: item.ref.eventId } : {}),
    ...(item.kind === 'booking' && readBooking(bookingOf(item)) ? { booking: readBooking(bookingOf(item)) } : {}),
    ...(bookingRef ? { bookingRef } : {}),
  };
}

/** Whether the Calendar offers the booking buttons at all does not depend on the clock, only which ones. */
function bookingRefOf(item: CalendarItem): CalendarItemCardBookingRef | undefined {
  const target = calendarBookingActionTarget(item, item.start);
  return target ? readBookingRef({ siteId: target.siteId, bookingId: target.record }) : undefined;
}

/** Its clients are the ones still on it; a cancelled or declined client is no longer coming. */
function bookingOf(item: Pick<CalendarItem, 'staff' | 'capacity' | 'booking' | 'people' | 'price' | 'sourceLabel'>): CalendarItemCardBooking {
  const active = (item.booking?.clients ?? []).filter((client) => client.status !== 'canceled' && client.status !== 'declined');
  const names = active.length > 0 ? active.map((client) => client.name) : (item.people ?? []).map((person) => person.name);
  return {
    staff: item.staff,
    ...(item.capacity ? { places: item.capacity } : { clients: names.filter((name): name is string => !!name) }),
    price: item.price,
    ...(!item.capacity && active.length === 1 && active[0].payment ? { payment: active[0].payment as CalendarItemCardBooking['payment'] } : {}),
    site: item.sourceLabel,
  };
}

const JOIN_OPENS_BEFORE_MS = 10 * 60_000;

/** What a meeting card offers at a moment: "Join call" from ten minutes before it starts, Maestro's Join / Skip / Undo
 * from the moment the card is drawn; both until it ends, after which they have nothing to do. */
export interface CardJoinOpen {
  call: boolean;
  decision: boolean;
}

export function cardJoinOpen(card: Pick<CalendarItemCard, 'start' | 'end'>, now: number): CardJoinOpen {
  const ongoing = now < card.end;
  return { call: ongoing && now >= card.start - JOIN_OPENS_BEFORE_MS, decision: ongoing };
}

/** "75 min": a booking's length, for the line under its title. */
export function cardMinutes(card: Pick<CalendarItemCard, 'start' | 'end' | 'allDay'>): number | undefined {
  if (card.allDay || card.end <= card.start) return undefined;
  return Math.round((card.end - card.start) / 60_000);
}
